import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  FileText, 
  Users, 
  Activity, 
  Calendar,
  Eye,
  TrendingUp,
  Database,
  ChevronLeft,
  ChevronRight,
  Trophy,
  ExternalLink
} from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import QuickLogin from '../components/QuickLogin';
import AdminNavigation from '../components/AdminNavigation';

const AdminPage = () => {
  const [demos, setDemos] = useState([]);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [demoSkins, setDemoSkins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skinsLoading, setSkinsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [demosPerPage] = useState(5);
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      const [demosResponse, statsResponse] = await Promise.all([
        adminAPI.getDemoFiles(1, 20, 'completed'),
        adminAPI.getStats()
      ]);
      
      setDemos(demosResponse.data.data || []);
      setStats(statsResponse.data.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDemoSkins = async (demoId) => {
    setSkinsLoading(true);
    try {
      const response = await adminAPI.getDemoSkins(demoId);
      setDemoSkins(response.data.data.skins);
      setSelectedDemo(demoId);
    } catch (error) {
      toast.error('Failed to load demo skins');
      console.error('Error loading demo skins:', error);
    } finally {
      setSkinsLoading(false);
    }
  };

  const getQualityBadgeClass = (quality) => {
    const qualityMap = {
      'highest': 'bg-green-600 text-white',
      'professional': 'bg-blue-600 text-white',
      'estimated': 'bg-yellow-600 text-black'
    };
    return qualityMap[quality] || 'bg-gray-600 text-white';
  };

  const getWearColor = (wearName) => {
    const wearMap = {
      'Factory New': 'text-green-400',
      'Minimal Wear': 'text-blue-400',
      'Field-Tested': 'text-yellow-400',
      'Well-Worn': 'text-orange-400',
      'Battle-Scarred': 'text-red-400'
    };
    return wearMap[wearName] || 'text-gray-400';
  };

  // Pagination logic
  const indexOfLastDemo = currentPage * demosPerPage;
  const indexOfFirstDemo = indexOfLastDemo - demosPerPage;
  const currentDemos = demos.slice(indexOfFirstDemo, indexOfLastDemo);
  const totalPages = Math.ceil(demos.length / demosPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          <QuickLogin />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center space-x-4"
      >
        <div className="p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
          <Shield className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400">Manage demos and skin data</p>
        </div>
      </motion.div>

      {/* Stats Cards */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <div className="card p-6">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-primary-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_demos}</div>
                <div className="text-sm text-gray-400">Total Demos</div>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center space-x-3">
              <Database className="h-8 w-8 text-green-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_skins}</div>
                <div className="text-sm text-gray-400">Total Skins</div>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8 text-blue-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_players}</div>
                <div className="text-sm text-gray-400">Players</div>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center space-x-3">
              <Activity className="h-8 w-8 text-purple-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_matches}</div>
                <div className="text-sm text-gray-400">Matches</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="card p-6"
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Activity className="h-5 w-5 text-blue-400" />
          <span>Data Sources</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/admin/hltv')}
            className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
          >
            <div className="flex items-center space-x-3">
              <Trophy className="h-6 w-6 text-white" />
              <div className="text-left">
                <div className="text-white font-semibold">HLTV Data</div>
                <div className="text-blue-200 text-sm">Teams, players & rankings</div>
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-white" />
          </button>

          <button
            onClick={() => navigate('/admin/matches')}
            className="flex items-center justify-between p-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg hover:from-red-700 hover:to-orange-700 transition-all duration-200"
          >
            <div className="flex items-center space-x-3">
              <Trophy className="h-6 w-6 text-white" />
              <div className="text-left">
                <div className="text-white font-semibold">Match Results</div>
                <div className="text-red-200 text-sm">HLTV match results with demos</div>
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-white" />
          </button>

          <button
            onClick={() => navigate('/admin/crawler')}
            className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
          >
            <div className="flex items-center space-x-3">
              <Database className="h-6 w-6 text-white" />
              <div className="text-left">
                <div className="text-white font-semibold">Crawler & Demo Processor</div>
                <div className="text-purple-200 text-sm">Automated data processing tools</div>
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-white" />
          </button>
          
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-600 to-teal-600 rounded-lg opacity-75">
            <div className="flex items-center space-x-3">
              <Database className="h-6 w-6 text-white" />
              <div className="text-left">
                <div className="text-white font-semibold">CS2 Demo Data</div>
                <div className="text-green-200 text-sm">Skins & match analysis</div>
              </div>
            </div>
            <div className="text-xs text-white bg-green-700 px-2 py-1 rounded">Current</div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
        {/* Demos List */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-3 card p-6"
        >
          <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
            <FileText className="h-5 w-5 text-primary-400" />
            <span>Demo Files</span>
          </h2>
          
          <div className="space-y-3">
            {currentDemos.map((demo) => (
              <div
                key={demo.id}
                className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer ${
                  selectedDemo === demo.id
                    ? 'bg-primary-600 border-primary-500'
                    : 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                }`}
                onClick={() => loadDemoSkins(demo.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">{demo.filename}</h3>
                  <Eye className="h-4 w-4 text-gray-400" />
                </div>
                
                <div className="text-sm text-gray-300 space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">HLTV Match</span>
                    <span className="text-gray-400">•</span>
                    <span>#{demo.hltv_match_id}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span>{demo.team_a} vs {demo.team_b}</span>
                    <span className="text-gray-400">•</span>
                    <span>{demo.map_name}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className={`px-2 py-1 rounded ${
                      demo.processing_status === 'completed' 
                        ? 'bg-green-600 text-green-100' 
                        : demo.processing_status === 'processing'
                        ? 'bg-yellow-600 text-yellow-100'
                        : 'bg-red-600 text-red-100'
                    }`}>
                      {demo.processing_status}
                    </span>
                    <span>{demo.skins_extracted} skins • {demo.players_found} players</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 flex items-center space-x-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === index + 1
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 flex items-center space-x-1"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </motion.div>

        {/* Demo Skins */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-7 card p-6"
        >
          <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-green-400" />
            <span>Demo Skins</span>
          </h2>

          {!selectedDemo ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Select a demo to view skins</p>
            </div>
          ) : skinsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="p-3 bg-gray-700 rounded-lg animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-600 rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-600 rounded w-3/4" />
                      <div className="h-3 bg-gray-600 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {demoSkins.map((skin, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-16 h-12 bg-gray-600 rounded overflow-hidden">
                        {skin.image_url ? (
                          <img
                            src={skin.image_url}
                            alt={`${skin.weapon} | ${skin.skin_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = `https://placehold.co/64x48/1a1a2e/ffffff/png?text=${encodeURIComponent(skin.weapon)}`;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-xs font-bold text-white">
                              {skin.weapon.slice(0, 3).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{skin.player_name}</div>
                        <div className="text-xs text-gray-400">{skin.team}</div>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getQualityBadgeClass(skin.quality)}`}>
                      {skin.quality}
                    </div>
                  </div>
                  
                  <div className="text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-primary-400 font-medium">
                        {skin.weapon} | {skin.skin_name}
                      </span>
                      <span className="text-gray-400">#{skin.paint_index}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className={getWearColor(skin.wear_name)}>
                        {skin.wear_name} ({skin.wear_float.toFixed(4)})
                      </span>
                      <span className="text-gray-400">
                        Used {skin.usage_count}x
                      </span>
                    </div>
                    
                    {skin.custom_name && (
                      <div className="text-xs text-yellow-400 italic">
                        "{skin.custom_name}"
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500">
                      Source: {skin.data_source} • Score: {skin.confidence_score}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
      </div>
    </>
  );
};

export default AdminPage;
